import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import CustomModal from "../../packages/CustomModal";
import DPCard from "../../packages/DPCard";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import DataVisualizationTable from "../../pages/analyseData/DataVisualizationTable";
import DataAppendList from "./DataAppendList";
import DataWarnings from "./DataWarnings";
import FileHandler from "./FileUploadModal";

import {
  CustomModalButtons,
  DATA_APPEND_CONSTANTS,
  DATA_APPEND_SECTION_ID,
  DATA_APPEND_WARNING_SEVERITY_TYPES,
} from "../../constants/global.constants";

import { DataAppendIcon, FileAdd } from "../../assets/icons";
import { append } from "../../assets/images";

import { navigateToCreateNewPlant } from "../../utils/Utils";

import {
  appendExistingFile,
  deleteAppendFile,
  downloadDatasetFile,
  getDataVisualization,
} from "../../network/cipAPI";

import "./DataAppend.scss";

const DataAppend = ({
  id,
  title,
  content,
  buttonText,
  linkText,
  fileId,
  selectedTrain,
  setIsLoading,
  fileName,
  className,
  mappedReference,
  isAppendFile,
  appendFileList,
  setAppendFileList,
  setIsAppendFile,
  appendStatus,
  setAppendStatus,
  setTempReferenceValues,
  ...props
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [formattedErrorContent, setFormattedErrorContent] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);
  const [viewLoading, setviewLoading] = useState(false);
  const [appendErrorSource, setAppendErrorSource] = useState(null); // 'large data gap error ' or 'no new data found eror'
  const [errorSource, setErrorSource] = useState(null); // 'upload' or 'append'
  const [warningRes, setWarningRes] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [viewAppendDialog, setViewAppendDialog] = useState(false);
  const [dataVisualisationRow, setDataVisualisationRow] = useState([]);
  const [lastUploadedDataset, setLastUploadedDataset] = useState(null);

  // Simple content type mapping for file extensions
  const getFileExtension = (contentType) => {
    if (contentType?.includes("spreadsheetml")) return "xlsx";
    if (contentType?.includes("excel")) return "xls";
    if (contentType?.includes("csv")) return "csv";
    return "xlsx";
  };

  const openDataVisualizationPopup = async (appendId) => {
    setviewLoading(true);
    setDataVisualisationRow([]);
    try {
      const response = await getDataVisualization(
        fileId,
        selectedTrain,
        appendId,
      );
      if (response && response.data) {
        if (response.data.detail && response.data.detail.error) {
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.somethingWrong"),
            "",
          );
        } else {
          setDataVisualisationRow(response.data);
        }
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setviewLoading(false);
      return setViewAppendDialog(true);
    }
  };
  // Helper function to trigger file download
  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleAddNewAppendedFile = () => {
    setIsFileUploadModalOpen(true);
  };

  const handleAppendRemove = async (appendId, setLoader) => {
    await deleteFileAndResetState(selectedTrain, appendId, setLoader);
  };

  const handleDatasetDownload = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await downloadDatasetFile(selectedTrain);

      if (response?.status !== 200 || !response?.data) {
        showErrorNotification(t("notificationStrings.somethingWrong"));
        return;
      }

      const contentType =
        response.headers?.["content-type"] ||
        response.headers?.["Content-Type"];
      const fileExtension = getFileExtension(contentType);
      const baseName = fileName || t("dataAppend.defaultFileName");
      const nameWithoutExt = baseName.replace(/\.[^/.]+$/, "");
      const downloadFileName = `${nameWithoutExt}.${fileExtension}`;

      triggerDownload(response.data, downloadFileName);
    } catch (error) {
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTertiaryButtonClick = () => {
    setIsFileUploadModalOpen(true);
  };

  const handleModalClose = async ({ target }) => {
    const id = target?.id;
    setShowErrorPopup(false);
    setFormattedErrorContent(null);
    setErrorSource(null);
    if (id === CustomModalButtons.CONFIRM) {
      await handleAppendExistingFile(true);
      setUploadedFile(null);
      setWarningRes(null);
    } else if (id === CustomModalButtons.CANCEL) {
      await handleDeleteFile(lastUploadedDataset?.appendId);
    } else {
      setIsFileUploadModalOpen(false);
    }
  };

  const handleFileValidation = (result) => {
    if (result?.success) {
      showSuccessNotification(
        result.message || t("notificationStrings.fileUploadSuccess"),
      );
    } else if (result?.error && result?.formattedContent) {
      setFormattedErrorContent(result.formattedContent);
      setErrorSource(DATA_APPEND_CONSTANTS.UPLOAD);
      setShowErrorPopup(true);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    } else if (result?.error) {
      showErrorNotification(result.error);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    }
  };

  const handleErrorPopupClose = async (buttonId) => {
    if (buttonId === CustomModalButtons.CONFIRM) {
      navigateToCreateNewPlant(navigate);
    } else if (buttonId === CustomModalButtons.CANCEL) {
      if (errorSource === DATA_APPEND_CONSTANTS.APPEND) {
        await handleDeleteFile(lastUploadedDataset?.appendId);
      } else {
        setShowErrorPopup(false);
        setFormattedErrorContent(null);
        setErrorSource(null);
        setIsFileUploadModalOpen(true);
        setTimeout(() => {
          const modalBody = document.querySelector(
            ".modal-body-container-append",
          );
          if (modalBody) {
            const input = modalBody.querySelector('input[type="file"]');
            if (input) input.click(); // trigger native file picker
          }
        }, 200); // small delay
      }
    } else if (buttonId === CustomModalButtons.CLOSE_BTN) {
      setIsFileUploadModalOpen(true);
      setShowErrorPopup(false);
      setFormattedErrorContent(null);
      setErrorSource(null);
    }
  };

  const deleteFileAndResetState = async (
    selectedTrain,
    appendId,
    setLoader,
  ) => {
    try {
      setLoader(true);
      const response = await deleteAppendFile(selectedTrain, appendId);
      if (response && response.message) {
        showSuccessNotification(response.message);
        setUploadedFile(null);
        setWarningRes(null);
        const updatedAppendList = appendFileList.filter(
          (file) => file.append_id !== appendId,
        );
        setAppendFileList(updatedAppendList);
        setIsAppendFile(updatedAppendList.length > 0);
        setAppendStatus(updatedAppendList.length > 0 ? "success" : "idle");
      } else {
        const errorMsg = response?.message || t("dataAppend.deleteFail");
        showErrorNotification(errorMsg);
      }
    } catch (error) {
      showErrorNotification(
        error.message || t("notificationStrings.somethingWrong"),
      );
    } finally {
      setLoader(false);
    }
  };
  const handleDeleteFile = async (appendId) => {
    await deleteFileAndResetState(selectedTrain, appendId, setErrorLoading);
    setShowErrorPopup(false);
    setFormattedErrorContent(null);
    setErrorSource(null);
    setIsFileUploadModalOpen(true);
  };

  const handleAppendResult = (result) => {
    setIsModalLoading(false);

    if (result?.success) {
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
      showSuccessNotification(
        result.message || t("notificationStrings.fileUploadSuccess"),
      );
    } else if (result?.error && result?.formattedContent) {
      // Set error content and show popup
      setFormattedErrorContent(result.formattedContent);
      setErrorSource(DATA_APPEND_CONSTANTS.APPEND);
      setShowErrorPopup(true);
      setUploadedFile(null);
      setIsFileUploadModalOpen(false);
      if (result.showNotification) {
        showErrorNotification(result.error);
      }
    } else if (result?.has_warnings) {
      setWarningRes(result);
    } else if (result?.error) {
      showErrorNotification(result.error);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    }
  };

  const errorPopupHeader = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.APPEND
        ? appendErrorSource === "large data"
          ? t("dataAppend.largeDataGapHeader")
          : t("dataAppend.noNewDataHeader")
        : t("dataAppend.appendDataFailed"),
    [errorSource, t],
  );

  const errorPopupCancelBtn = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.APPEND
        ? appendErrorSource === "no new data"
          ? t("ValueStrings.cancel")
          : t("dataAppend.cancelAppend")
        : t("dataAppend.uploadAnotherFile"),
    [errorSource, appendErrorSource, t],
  );

  const errorPopupLinkBtn = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.UPLOAD
        ? t("dataAppend.cancelAppend")
        : undefined,
    [errorSource, t],
  );

  // Memoize confirmBtn label outside of JSX
  const confirmBtnLabel = useMemo(() => t("dataAppend.createNewPlant"), [t]);

  const handleAppendExistingFile = async (proceedOnWarning = false) => {
    let result = null;

    try {
      setIsModalLoading(true);

      const lastUploadAppendId = lastUploadedDataset?.appendId;
      const response = await appendExistingFile(
        selectedTrain,
        lastUploadAppendId,
        proceedOnWarning,
      );

      if (response?.status === 200) {
        const { filename, appended_at, append_id, data_summary, user_id } =
          response;
        const { start, end, total_records } = data_summary?.new_data_range;

        const filteredAppendFileList = appendFileList.filter(
          (file) => file.append_id !== append_id,
        );

        const newAppendFileData = {
          append_id,
          user_id,
          appended_at,
          file_name: filename,
          start_date: start,
          end_date: end,
          no_of_records: total_records,
        };
        setAppendFileList([...filteredAppendFileList, newAppendFileData]);
        setIsAppendFile(response?.isAppend);
        setAppendStatus("success");
        setLastUploadedDataset(null);
        result = {
          success: true,
          message: response.message,
        };
      } else if (response?.status === 422 || response?.status === 400) {
        setAppendStatus("error");
        // Check for critical warnings first
        if (response.detail?.has_warnings && response.detail?.warnings) {
          const { warnings, data_summary, has_warnings } = response.detail;
          const criticalWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.CRITICAL,
          );
          const gapWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.WARNING,
          );
          const overlapWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.INFO,
          );

          if (criticalWarning) {
            const criticalWarningMessage = criticalWarning.message;
            setAppendErrorSource("large data");
            const formattedContent = (
              <div>
                <div className="marginBtm10">{criticalWarningMessage}</div>
                <div>{t("dataAppend.newPlantText")} </div>
              </div>
            );
            result = {
              error: criticalWarningMessage,
              formattedContent,
              showNotification: false,
            };
          } else if (gapWarning || overlapWarning) {
            result = { gapWarning, overlapWarning, data_summary, has_warnings };
          }
        } else if (response.detail?.key === "NO_DATA_FOUND") {
          setAppendErrorSource("no new data");
          const message = response?.detail?.error;
          const formattedContent = (
            <div>
              <div className="marginBtm10">{t("dataAppend.noNewDataBody")}</div>
              <div className="preWrap">
                {t("dataAppend.noNewDataSuggestion")}
              </div>
            </div>
          );
          result = {
            error: message,
            formattedContent,
            showNotification: false,
          };
        } else {
          const errorMessage =
            response.detail?.error ||
            response.message ||
            t("dataAppend.invalidFileError");
          result = {
            error: errorMessage,
            showNotification: true,
          };
        }
      } else {
        setAppendStatus("error");
        result = { error: t("ValueStrings.somethingWentWrong") };
      }
    } catch (error) {
      setAppendStatus("error");
      result = { error: error.message || t("dataAppend.appendError") };
    } finally {
      handleAppendResult(result);
    }
  };

  const handleViewAppendTableOpen = (appendId) => {
    setViewAppendDialog(true);
    openDataVisualizationPopup(appendId);
  };

  const handleViewAppendTableClose = () => {
    setViewAppendDialog(false);
  };

  const filteredAppendFileList = useMemo(() => {
    const validDatasets = appendFileList.filter((ds) => ds && ds.start_date);
    return validDatasets.sort(
      (a, b) => new Date(a.appended_at) - new Date(b.appended_at),
    );
  }, [appendFileList]);

  useEffect(() => {
    if (appendFileList.length === 0) {
      setLastUploadedDataset(null);
      return;
    }
    const uploadOnlyDataset = appendFileList.find(
      (ds) => ds && ds.start_date === null,
    );

    if (uploadOnlyDataset) {
      setLastUploadedDataset({
        fileName: uploadOnlyDataset.file_name,
        appendId: uploadOnlyDataset.append_id,
      });
    } else {
      setLastUploadedDataset(null);
    }
  }, [appendFileList]);

  return (
    <div id={DATA_APPEND_SECTION_ID}>
      {isAppendFile && filteredAppendFileList.length > 0 ? (
        <div className="ro-card-wrapper">
          <div className="ro-card-header">
            <div className="teal-heading">
              {t("dataAppend.appendHeaderSuccess")} (
              {filteredAppendFileList.length})
            </div>

            <WPTertiaryButton
              variant="contained"
              type="secondary"
              size="small"
              label={t("dataAppend.appendNewData")}
              icon={<FileAdd aria-label="Add File Icon" />}
              iconPlacement="before"
              onClick={handleAddNewAppendedFile}
              className="append-new-data-btn"
            />
            {/* <WPTertiaryButton
              variant="text"
              label={t("dataAppend.viewAppend")}
              size="small"
              onClick={handleViewAppendTableOpen}
            /> */}
          </div>
          <DataAppendList
            datasets={filteredAppendFileList}
            onViewData={handleViewAppendTableOpen}
            onDelete={(appendId) => handleAppendRemove(appendId, setIsLoading)}
          />
        </div>
      ) : (
        <DPCard
          id={id}
          disabled={Boolean(!mappedReference)}
          className={`data-append-card ${className || ""}`}
          {...props}
        >
          <div className="data-append-wrapper">
            <div className="data-append-icon">
              {/* Main append icon from external SVG file */}
              <img src={append} alt="Append Data" />
            </div>

            {/* Middle Text Content */}
            <div className="data-append-content">
              <div className="data-append-header">
                {title && <p className="data-append-title">{title}</p>}
              </div>
              <div className="data-append-body">
                {linkText ? (
                  <p className="data-append-text">
                    {content}{" "}
                    <span
                      className="data-append-link"
                      style={{ cursor: "pointer" }}
                      onClick={
                        !mappedReference ? undefined : handleDatasetDownload
                      }
                    >
                      {linkText}
                    </span>
                    .
                  </p>
                ) : (
                  <p className="data-append-text">{content}</p>
                )}
              </div>
            </div>

            {/* Button Section */}
            <div className="data-append-button">
              <WPTertiaryButton
                id="append-data-btn"
                label={buttonText}
                icon={<DataAppendIcon aria-label="Append Data Icon" />}
                iconPlacement="before"
                className="data-append-tertiary-btn"
                variant="text"
                onClick={handleTertiaryButtonClick}
                disabled={Boolean(!mappedReference)}
              />
            </div>
          </div>
        </DPCard>
      )}

      {/* File Upload Modal */}
      {isFileUploadModalOpen && (
        <CustomModal
          header={t("dataAppend.appendData")}
          onModalClose={handleModalClose}
          custombodyclass={"modal-body-container-append"}
          isLoading={isModalLoading}
          hideFooter={warningRes === null}
          confirmBtn={t("ValueStrings.continueAnyway")}
          cancelBtn={t("dataAppend.cancelAppend")}
        >
          {warningRes === null ? (
            <FileHandler
              onFileValidation={handleFileValidation}
              handleAppendExistingFile={handleAppendExistingFile}
              selectedTrain={selectedTrain}
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              handleAppendRemove={() =>
                handleAppendRemove(
                  lastUploadedDataset?.appendId,
                  setIsModalLoading,
                )
              }
              lastUploadedDataset={lastUploadedDataset}
              setLastUploadedDataset={setLastUploadedDataset}
            />
          ) : (
            <DataWarnings
              gapWarning={warningRes.gapWarning}
              overlapWarning={warningRes.overlapWarning}
              summaryData={warningRes.data_summary}
              selectedTrain={selectedTrain}
            />
          )}
        </CustomModal>
      )}

      {/* Error Confirmation Popup */}
      {showErrorPopup && (
        <ConfirmationPopup
          header={errorPopupHeader}
          description={formattedErrorContent}
          type={DIALOG_TYPES.ERROR}
          onClose={handleErrorPopupClose}
          confirmBtn={confirmBtnLabel}
          cancelBtn={errorPopupCancelBtn}
          linkBtn={errorPopupLinkBtn}
          width="700px"
          isLoading={errorLoading}
        />
      )}

      {viewAppendDialog && (
        <DataVisualizationTable
          open={viewAppendDialog}
          onClose={handleViewAppendTableClose}
          title={t("dataAppend.viewAppend")}
          rows={dataVisualisationRow}
          setshowDataVisualizationPopup={setViewAppendDialog}
          setTempReferenceValues={setTempReferenceValues}
          isLoading={viewLoading}
        />
      )}
    </div>
  );
};

export default DataAppend;
